import React, { useState, useEffect } from 'react';
import { X, RefreshCw } from 'lucide-react';
import moment from 'moment';
import { API_URL } from '../services/ApiService';
import { getDateOrder, formatDateTime, getMomentFormat } from '../utils/dateFormatter';

const EventModal = ({ isOpen, onClose, onSave, initialDate, eventData: existingEvent, isEditMode = false }) => {
  const [showEditScopeDialog, setShowEditScopeDialog] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);
  const [eventData, setEventData] = useState({
    type: 'rehearsal',
    title: '',
    description: '',
    startTime: initialDate ? moment(initialDate).format('YYYY-MM-DDTHH:mm') : moment().format('YYYY-MM-DDTHH:mm'),
    endTime: initialDate ? moment(initialDate).add(2, 'hours').format('YYYY-MM-DDTHH:mm') : moment().add(2, 'hours').format('YYYY-MM-DDTHH:mm'),
    location: {
      name: '',
      address: ''
    },
    band: '',
    setlist: '',
    isPublic: true,  // Default to public for free users
    recurring: false,
    recurrenceRule: {
      frequency: 'weekly',
      daysOfWeek: [],
      endDate: moment().add(1, 'month').format('YYYY-MM-DD') // Default to one month from now
    }
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userBands, setUserBands] = useState([]);
  const [bandSetlists, setBandSetlists] = useState([]);

  // Initialize form data when editing
  useEffect(() => {
    if (isEditMode && existingEvent) {
      setEventData({
        type: existingEvent.type || 'rehearsal',
        title: existingEvent.title || '',
        description: existingEvent.description || '',
        startTime: moment(existingEvent.startTime).format('YYYY-MM-DDTHH:mm'),
        endTime: moment(existingEvent.endTime).format('YYYY-MM-DDTHH:mm'),
        location: existingEvent.location || { name: '', address: '' },
        band: existingEvent.band?._id || existingEvent.band || '',
        setlist: existingEvent.setlist?._id || existingEvent.setlist || '',
        isPublic: existingEvent.isPublic !== undefined ? existingEvent.isPublic : true,  // Default to public
        recurring: existingEvent.recurring || false,
        recurrenceRule: existingEvent.recurrenceRule ? {
          frequency: existingEvent.recurrenceRule.frequency || 'weekly',
          daysOfWeek: existingEvent.recurrenceRule.daysOfWeek || [],
          endDate: existingEvent.recurrenceRule.endDate 
            ? moment(existingEvent.recurrenceRule.endDate).format('YYYY-MM-DD')
            : moment(existingEvent.startTime).add(1, 'month').format('YYYY-MM-DD')
        } : {
          frequency: 'weekly',
          daysOfWeek: [],
          endDate: moment(existingEvent.startTime).add(1, 'month').format('YYYY-MM-DD')
        }
      });
    }
  }, [isEditMode, existingEvent]);

  useEffect(() => {
    // Configure moment based on system locale
    const dateOrder = getDateOrder();
    
    // Set moment locale with proper week start
    moment.locale('en', {
      week: {
        dow: dateOrder === 'MDY' ? 0 : 1, // Sunday for US format, Monday for others
      }
    });
    
    // Also try to set the browser's locale for datetime-local inputs
    // This helps some browsers display the correct format
    if (dateOrder === 'DMY') {
      document.documentElement.lang = 'en-GB';
    } else {
      document.documentElement.lang = 'en-US';
    }
  }, []);
  
  // Fetch setlists when band changes
  useEffect(() => {
    const fetchSetlists = async () => {
      if (!eventData.band) {
        setBandSetlists([]);
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await fetch(`${API_URL}/setlists?band=${eventData.band}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        const data = await response.json();
        if (data.success) {
          setBandSetlists(data.data || []);
        }
      } catch (error) {
        console.error('Error fetching setlists:', error);
      }
    };
    
    fetchSetlists();
  }, [eventData.band]);
  
  useEffect(() => {
    // Fetch user's bands
    const fetchBands = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          return;
        }
        
        const response = await fetch(`${API_URL}/bands`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        const data = await response.json();
        
        if (data.success) {
          setUserBands(data.data || []);
          // Set first band as default if available
          if (data.data && data.data.length > 0) {
            setEventData(prev => ({ ...prev, band: data.data[0]._id }));
          }
        }
      } catch (error) {
        console.error('Error fetching bands:', error);
      }
    };
    
    if (isOpen) {
      fetchBands();
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!eventData.title) {
      setError('Title is required');
      return;
    }
    
    if (!eventData.band) {
      setError('Please select a band');
      return;
    }
    
    if (new Date(eventData.endTime) <= new Date(eventData.startTime)) {
      setError('End time must be after start time');
      return;
    }
    
    // If editing a recurring instance, check what changed
    if (isEditMode && existingEvent?.isRecurringInstance) {
      // Check if only the setlist changed
      const onlySetlistChanged = 
        eventData.title === existingEvent.title &&
        eventData.description === existingEvent.description &&
        eventData.type === existingEvent.type &&
        moment(eventData.startTime).isSame(moment(existingEvent.startTime)) &&
        moment(eventData.endTime).isSame(moment(existingEvent.endTime)) &&
        JSON.stringify(eventData.location) === JSON.stringify(existingEvent.location) &&
        eventData.isPublic === existingEvent.isPublic &&
        eventData.setlist !== (existingEvent.setlist?._id || existingEvent.setlist || '');
      
      if (onlySetlistChanged) {
        // For setlist-only changes, ask if they want to update just this or all
        setPendingSubmitData(eventData);
        setShowEditScopeDialog(true);
        return;
      }
      
      // For other changes, show the edit scope dialog
      setPendingSubmitData(eventData);
      setShowEditScopeDialog(true);
      return;
    }
    
    // For parent events, use 'all' scope to update the series
    // For other events, use 'single' scope
    const scope = (isEditMode && existingEvent?.recurring) ? 'all' : 'single';
    await saveEvent(eventData, scope);
  };
  
  const saveEvent = async (eventDataToSave, editScope) => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      
      const requestBody = {
        ...eventDataToSave,
        startTime: new Date(eventDataToSave.startTime),
        endTime: new Date(eventDataToSave.endTime),
        ...(isEditMode && { editScope }) // Add editScope when editing
      };
      
      // Don't send recurring/recurrenceRule for parent events unless they actually changed
      if (isEditMode && existingEvent?.recurring && !existingEvent?.isRecurringInstance) {
        // Never change the recurring flag for parent events
        delete requestBody.recurring;
        
        // Check if recurrence rules actually changed
        const existingRule = existingEvent.recurrenceRule;
        const newRule = eventDataToSave.recurrenceRule;
        
        const rulesMatch = existingRule && newRule && 
          existingRule.frequency === newRule.frequency &&
          JSON.stringify(existingRule.daysOfWeek) === JSON.stringify(newRule.daysOfWeek) &&
          moment(existingRule.endDate).format('YYYY-MM-DD') === moment(newRule.endDate).format('YYYY-MM-DD');
        
        if (rulesMatch) {
          // Rules haven't changed, don't send them
          delete requestBody.recurrenceRule;
        }
      }
      
      // Clean up empty string values that should be null/undefined
      if (!requestBody.setlist || requestBody.setlist === '') {
        delete requestBody.setlist;
      }
      if (!requestBody.band || requestBody.band === '') {
        delete requestBody.band;
      }
      
      const url = isEditMode ? `${API_URL}/events/${existingEvent._id}` : `${API_URL}/events`;
      const method = isEditMode ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });
      
      const data = await response.json();
      
      if (data.success) {
        if (onSave) {
          onSave(data.data);
        }
        onClose();
        setShowEditScopeDialog(false);
        setPendingSubmitData(null);
        // Reset form
        setEventData({
          type: 'rehearsal',
          title: '',
          description: '',
          startTime: moment().format('YYYY-MM-DDTHH:mm'),
          endTime: moment().add(2, 'hours').format('YYYY-MM-DDTHH:mm'),
          location: {
            name: '',
            address: ''
          },
          band: userBands.length > 0 ? userBands[0]._id : '',
          setlist: '',
          isPublic: true,  // Default to public for free users
          recurring: false,
          recurrenceRule: {
            frequency: 'weekly',
            daysOfWeek: [],
            endDate: moment().add(1, 'month').format('YYYY-MM-DD')
          }
        });
      } else {
        setError(data.error || 'Failed to create event');
      }
    } catch (error) {
      console.error('Error creating event:', error);
      setError(`Failed to create event: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleEditScopeChoice = async (scope) => {
    setShowEditScopeDialog(false);
    if (pendingSubmitData) {
      await saveEvent(pendingSubmitData, scope);
      setPendingSubmitData(null);
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">{isEditMode ? 'Edit Event' : 'Create Event'}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Event Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event Type
              </label>
              <select
                value={eventData.type}
                onChange={(e) => setEventData({ ...eventData, type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="rehearsal">Rehearsal</option>
                <option value="concert">Concert</option>
                <option value="recording">Recording Session</option>
                <option value="meeting">Meeting</option>
                <option value="audition">Audition</option>
              </select>
            </div>
            
            {/* Band */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Band
              </label>
              <select
                value={eventData.band}
                onChange={(e) => setEventData({ ...eventData, band: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select a band</option>
                {userBands.map(band => (
                  <option key={band._id} value={band._id}>
                    {band.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Setlist (optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Setlist (Optional)
                {existingEvent?.isRecurringInstance && !existingEvent?.hasOwnSetlist && existingEvent?.inheritedSetlist && (
                  <span className="ml-2 text-xs text-blue-600">
                    (Using series default)
                  </span>
                )}
              </label>
              <select
                value={eventData.setlist}
                onChange={(e) => setEventData({ ...eventData, setlist: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">
                  {existingEvent?.isRecurringInstance ? 'Use series default' : 'No setlist'}
                </option>
                {bandSetlists.map(setlist => (
                  <option key={setlist._id} value={setlist._id}>
                    {setlist.name} ({setlist.sheets?.length || 0} songs)
                  </option>
                ))}
              </select>
              {eventData.band && bandSetlists.length === 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  No setlists found for this band. Create a setlist first to associate it with events.
                </p>
              )}
            </div>
            
            {/* Title */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title
              </label>
              <input
                type="text"
                value={eventData.title}
                onChange={(e) => setEventData({ ...eventData, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Event title"
                required
              />
            </div>
            
            {/* Description */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={eventData.description}
                onChange={(e) => setEventData({ ...eventData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Event description (optional)"
              />
            </div>
            
            {/* Start Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Time
              </label>
              <input
                type="datetime-local"
                value={eventData.startTime}
                onChange={(e) => setEventData({ ...eventData, startTime: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                lang={getDateOrder() === 'DMY' ? 'en-GB' : 'en-US'}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                {eventData.startTime && formatDateTime(eventData.startTime)}
              </p>
            </div>
            
            {/* End Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Time
              </label>
              <input
                type="datetime-local"
                value={eventData.endTime}
                onChange={(e) => setEventData({ ...eventData, endTime: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                lang={getDateOrder() === 'DMY' ? 'en-GB' : 'en-US'}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                {eventData.endTime && formatDateTime(eventData.endTime)}
              </p>
            </div>
            
            {/* Location Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location Name
              </label>
              <input
                type="text"
                value={eventData.location.name}
                onChange={(e) => setEventData({
                  ...eventData,
                  location: { ...eventData.location, name: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Venue or location name"
              />
            </div>
            
            {/* Location Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <input
                type="text"
                value={eventData.location.address}
                onChange={(e) => setEventData({
                  ...eventData,
                  location: { ...eventData.location, address: e.target.value }
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Street address"
              />
            </div>
            
            {/* Public Event */}
            <div className="md:col-span-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={eventData.isPublic}
                  onChange={(e) => setEventData({ ...eventData, isPublic: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-gray-700">
                  Make this event public
                </span>
              </label>
            </div>
            
            {/* Recurring Event - show for all, but disable for instances */}
            <div className="md:col-span-2">
              {existingEvent?.isRecurringInstance ? (
                <div className="flex items-center">
                  <RefreshCw className="w-4 h-4 mr-2 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">
                    Part of a recurring series
                  </span>
                  <span className="text-xs text-gray-500 ml-2">
                    (Edit options available when saving)
                  </span>
                </div>
              ) : (
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={eventData.recurring}
                    onChange={(e) => setEventData({ ...eventData, recurring: e.target.checked })}
                    className="mr-2"
                  />
                  <RefreshCw className="w-4 h-4 mr-1 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">
                    Recurring event
                  </span>
                </label>
              )}
            </div>
            
            {/* Recurrence Options */}
            {eventData.recurring && (
              <div className="md:col-span-2 p-4 border border-gray-200 rounded-lg space-y-4">
                <h3 className="font-medium text-gray-900">Recurrence Settings</h3>
                
                {/* Frequency */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Repeat
                  </label>
                  <select
                    value={eventData.recurrenceRule.frequency}
                    onChange={(e) => setEventData({
                      ...eventData,
                      recurrenceRule: { ...eventData.recurrenceRule, frequency: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Bi-weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                
                {/* Days of Week (for weekly/biweekly) */}
                {(eventData.recurrenceRule.frequency === 'weekly' || eventData.recurrenceRule.frequency === 'biweekly') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Repeat on
                    </label>
                    <div className="flex gap-2">
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => (
                        <label key={day} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={eventData.recurrenceRule.daysOfWeek?.includes(index + 1) || false}
                            onChange={(e) => {
                              const days = eventData.recurrenceRule.daysOfWeek || [];
                              const dayNum = index + 1;
                              const newDays = e.target.checked 
                                ? [...days, dayNum]
                                : days.filter(d => d !== dayNum);
                              setEventData({
                                ...eventData,
                                recurrenceRule: { ...eventData.recurrenceRule, daysOfWeek: newDays }
                              });
                            }}
                            className="mr-1"
                          />
                          <span className="text-sm">{day}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* End Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End recurrence
                  </label>
                  <input
                    type="date"
                    value={eventData.recurrenceRule.endDate}
                    onChange={(e) => setEventData({
                      ...eventData,
                      recurrenceRule: { ...eventData.recurrenceRule, endDate: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Events will be created until this date</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Action Buttons */}
          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Event' : 'Create Event')}
            </button>
          </div>
        </form>
      </div>
    </div>
    
    {/* Edit Scope Dialog for Recurring Events */}
    {showEditScopeDialog && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-lg p-6 max-w-md w-full border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Edit Recurring Event</h3>
          <p className="text-gray-600 mb-6">
            This event is part of a recurring series. How would you like to apply your changes?
          </p>
          
          <div className="space-y-3">
            <button
              onClick={() => handleEditScopeChoice('single')}
              className="w-full p-4 text-left border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
            >
              <div className="font-medium mb-1">Just this event</div>
              <div className="text-sm text-gray-500">
                Changes will only apply to this occurrence and it will be removed from the series
              </div>
            </button>
            
            <button
              onClick={() => handleEditScopeChoice('future')}
              className="w-full p-4 text-left border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
            >
              <div className="font-medium mb-1">This and all future events</div>
              <div className="text-sm text-gray-500">
                Changes will apply from this event forward
              </div>
            </button>
            
            <button
              onClick={() => handleEditScopeChoice('all')}
              className="w-full p-4 text-left border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
            >
              <div className="font-medium mb-1">All events in the series</div>
              <div className="text-sm text-gray-500">
                Changes will apply to all events in this recurring series
              </div>
            </button>
          </div>
          
          <button
            onClick={() => {
              setShowEditScopeDialog(false);
              setPendingSubmitData(null);
            }}
            className="mt-4 w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
        </div>
      </div>
    )}
    </>
  );
};

export default EventModal;